import React, { useState, useRef, useEffect } from "react";
import { useContentUpload } from "../../../hooks/useContentUpload";
import { useContentsList, Content } from "../../../hooks/useContentsList";
import { useContentDelete } from "../../../hooks/useContentDelete";

interface ConteudosTabProps {
  onPreviewContent?: (content: Content) => void;
}

const ConteudosTab: React.FC<ConteudosTabProps> = ({ onPreviewContent }) => {
  // Estados locais para a aba de conteúdos
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [contentName, setContentName] = useState<string>("");
  const [contentDescription, setContentDescription] = useState<string>("");
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 10;

  // Estados para modal de confirmação de delete
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [contentToDelete, setContentToDelete] = useState<Content | null>(null);

  // Hooks específicos para conteúdos
  const { uploadContent, uploadState, resetUploadState } = useContentUpload();
  const {
    contents,
    isLoading: contentsLoading,
    error: contentsError,
    fetchContents,
  } = useContentsList();
  const { deleteContent, isLoading: deleteLoading } = useContentDelete();

  // Buscar conteúdos ao montar o componente
  useEffect(() => {
    fetchContents();
  }, []);

  // Funções específicas para conteúdos
  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setSelectedFile(file);
      // Se não há nome personalizado, usar o nome do arquivo
      if (!contentName.trim()) {
        setContentName(file.name.replace(/\.[^/.]+$/, "")); // Remove extensão
      }
      resetUploadState();
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    const file = e.dataTransfer.files[0];
    if (file) {
      setSelectedFile(file);
      // Se não há nome personalizado, usar o nome do arquivo
      if (!contentName.trim()) {
        setContentName(file.name.replace(/\.[^/.]+$/, "")); // Remove extensão
      }
      resetUploadState();
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) return;

    const success = await uploadContent(
      selectedFile,
      contentName,
      contentDescription
    );
    if (success) {
      setSelectedFile(null);
      setContentName("");
      setContentDescription("");
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
      fetchContents();
    }
  };

  const handlePreviewContent = (content: Content) => {
    if (onPreviewContent) {
      onPreviewContent(content);
    }
  };

  const handleDeleteClick = (content: Content) => {
    setContentToDelete(content);
    setShowDeleteModal(true);
  };

  const handleConfirmDelete = async () => {
    if (!contentToDelete) return;

    const success = await deleteContent(contentToDelete.id);
    if (success) {
      fetchContents();

      // Ajustar página atual se necessário
      const newTotalPages = Math.ceil((contents.length - 1) / pageSize);
      if (currentPage > newTotalPages && newTotalPages > 0) {
        setCurrentPage(newTotalPages);
      }
    }

    setShowDeleteModal(false);
    setContentToDelete(null);
  };

  const handleCancelDelete = () => {
    setShowDeleteModal(false);
    setContentToDelete(null);
  };

  // Paginação
  const totalPages = Math.ceil(contents.length / pageSize);
  const getPaginatedContents = () => {
    const startIndex = (currentPage - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    return contents.slice(startIndex, endIndex);
  };

  // Loading state
  if (contentsLoading) {
    return (
      <div className="loading-container">
        <div className="spinner"></div>
        <span>Carregando conteúdos...</span>
      </div>
    );
  }

  // Error state
  if (contentsError) {
    return <div className="error-message">{contentsError}</div>;
  }

  return (
    <div className="conteudos-tab">
      <h2>Gerenciar Conteúdos</h2>
      <p>Faça upload de arquivos para o sistema</p>

      <div className="upload-section">
        <div
          className="upload-area"
          onDragOver={handleDragOver}
          onDrop={handleDrop}
          onClick={() => fileInputRef.current?.click()}
        >
          <div className="upload-content">
            <div className="upload-icon">📁</div>
            <h3>Arraste arquivos aqui ou clique para selecionar</h3>
            <p>Suporte para TXT, PDF, PNG, JPG, MP4, MP3, DOCX, XLSX, PPTX</p>
          </div>
        </div>

        <input
          ref={fileInputRef}
          type="file"
          onChange={handleFileSelect}
          style={{ display: "none" }}
          accept=".txt,.pdf,.png,.jpg,.jpeg,.mp4,.mp3,.docx,.xlsx,.pptx"
        />

        {selectedFile && (
          <div className="content-form">
            <div className="form-group">
              <label htmlFor="contentName">Nome do Conteúdo:</label>
              <input
                id="contentName"
                type="text"
                value={contentName}
                onChange={(e) => setContentName(e.target.value)}
                placeholder="Digite o nome do conteúdo (opcional)"
                className="form-input"
              />
              {!contentName.trim() && (
                <small className="form-help">
                  💡 Se não informar, será usado o nome do arquivo
                </small>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="contentDescription">Descrição:</label>
              <textarea
                id="contentDescription"
                value={contentDescription}
                onChange={(e) => setContentDescription(e.target.value)}
                placeholder="Digite uma descrição para o conteúdo (opcional)"
                className="form-textarea"
                rows={3}
              />
              {!contentDescription.trim() && (
                <small className="form-help">
                  💡 Se não informar, será usado "Arquivo: [nome do arquivo]"
                </small>
              )}
            </div>
          </div>
        )}

        {selectedFile && (
          <div className="file-info">
            <div className="file-details">
              <span className="file-name">{selectedFile.name}</span>
              <span className="file-size">
                {(selectedFile.size / 1024 / 1024).toFixed(2)} MB
              </span>
            </div>
            <button
              className="upload-button"
              onClick={handleUpload}
              disabled={uploadState.isUploading}
            >
              {uploadState.isUploading ? "Enviando..." : "Enviar Arquivo"}
            </button>
          </div>
        )}

        {uploadState.isUploading && (
          <div className="upload-progress">
            <div className="progress-bar">
              <div
                className="progress-fill"
                style={{ width: `${uploadState.progress}%` }}
              ></div>
            </div>
            <span className="progress-text">{uploadState.progress}%</span>
          </div>
        )}

        {uploadState.error && (
          <div className="upload-error">❌ {uploadState.error}</div>
        )}

        {uploadState.success && (
          <div className="upload-success">✅ Upload concluído com sucesso!</div>
        )}
      </div>

      {/* Lista de Conteúdos */}
      <div className="contents-list-section">
        <h3>📋 Meus Conteúdos</h3>

        {contents.length === 0 ? (
          <p>
            Nenhum conteúdo encontrado. Faça upload de arquivos para começar!
          </p>
        ) : (
          <>
            <div className="contents-table-container">
              <table className="contents-table">
                <thead>
                  <tr>
                    <th>Nome</th>
                    <th>Tipo</th>
                    <th>Data Criação</th>
                    <th>Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {getPaginatedContents().map((content) => (
                    <tr key={content.id} className="content-row">
                      <td className="content-name-cell">
                        <div className="content-name-wrapper">
                          <span className="content-name">{content.name}</span>
                          {content.description && (
                            <span className="content-description">
                              {content.description}
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="content-type-cell">
                        <span className="content-type-badge">
                          {content.type}
                        </span>
                      </td>
                      <td className="content-date-cell">
                        {new Date(content.createdAt).toLocaleDateString(
                          "pt-BR"
                        )}
                      </td>
                      <td className="content-actions-cell">
                        <div className="action-buttons">
                          <button
                            className="preview-content-btn"
                            onClick={() => handlePreviewContent(content)}
                            title="Visualizar conteúdo"
                          >
                            👁️
                          </button>
                          <button
                            className="delete-content-btn"
                            onClick={() => handleDeleteClick(content)}
                            disabled={deleteLoading}
                            title="Deletar conteúdo"
                          >
                            🗑️
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Controles de Paginação */}
            {totalPages > 1 && (
              <div className="pagination-controls">
                <div className="pagination-info">
                  Página {currentPage} de {totalPages} ({contents.length}{" "}
                  conteúdos)
                </div>

                <div className="pagination-buttons">
                  {/* Botão Anterior */}
                  {currentPage > 1 && (
                    <button
                      className="pagination-btn"
                      onClick={() => setCurrentPage(currentPage - 1)}
                    >
                      ← Anterior
                    </button>
                  )}

                  {/* Números das páginas */}
                  {Array.from({ length: totalPages }, (_, i) => i + 1)
                    .filter((page) => {
                      // Mostrar apenas algumas páginas ao redor da atual
                      return (
                        page === 1 ||
                        page === totalPages ||
                        (page >= currentPage - 1 && page <= currentPage + 1)
                      );
                    })
                    .map((page, index, array) => (
                      <React.Fragment key={page}>
                        {/* Adicionar "..." se houver gap */}
                        {index > 0 && array[index - 1] !== page - 1 && (
                          <span className="pagination-dots">...</span>
                        )}

                        <button
                          className={`pagination-btn ${
                            currentPage === page ? "active" : ""
                          }`}
                          onClick={() => setCurrentPage(page)}
                        >
                          {page}
                        </button>
                      </React.Fragment>
                    ))}

                  {/* Botão Próxima */}
                  {currentPage < totalPages && (
                    <button
                      className="pagination-btn"
                      onClick={() => setCurrentPage(currentPage + 1)}
                    >
                      Próxima →
                    </button>
                  )}
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Modal de Confirmação de Delete */}
      {showDeleteModal && contentToDelete && (
        <div
          className="modal-overlay"
          onClick={handleCancelDelete}
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0, 0, 0, 0.7)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 99999,
          }}
        >
          <div
            className="modal-content"
            onClick={(e) => e.stopPropagation()}
            style={{
              backgroundColor: "#2B2B2B",
              padding: "30px",
              borderRadius: "12px",
              maxWidth: "450px",
              width: "90%",
              boxShadow: "0 4px 20px rgba(0, 0, 0, 0.3)",
            }}
          >
            <div style={{ marginBottom: "20px" }}>
              <h2
                style={{
                  color: "#fff",
                  fontSize: "24px",
                  marginBottom: "10px",
                  fontWeight: 500,
                }}
              >
                🗑️ Confirmar Exclusão
              </h2>
              <p
                style={{ color: "#D9D9D9", fontSize: "16px", lineHeight: 1.6 }}
              >
                Tem certeza que deseja deletar o conteúdo{" "}
                <strong style={{ color: "#fff" }}>
                  "{contentToDelete.name}"
                </strong>
                ?
              </p>
              <p
                style={{
                  color: "#FF9800",
                  fontSize: "14px",
                  marginTop: "10px",
                }}
              >
                ⚠️ Esta ação não pode ser desfeita.
              </p>
            </div>

            <div
              style={{
                display: "flex",
                gap: "12px",
                justifyContent: "flex-end",
              }}
            >
              <button
                onClick={handleCancelDelete}
                style={{
                  padding: "10px 20px",
                  backgroundColor: "#4CAF50",
                  color: "#fff",
                  border: "none",
                  borderRadius: "6px",
                  cursor: "pointer",
                  fontSize: "16px",
                  fontWeight: 500,
                  transition: "all 0.3s ease",
                }}
                onMouseEnter={(e) =>
                  (e.currentTarget.style.backgroundColor = "#45a049")
                }
                onMouseLeave={(e) =>
                  (e.currentTarget.style.backgroundColor = "#4CAF50")
                }
              >
                Cancelar
              </button>
              <button
                onClick={handleConfirmDelete}
                disabled={deleteLoading}
                style={{
                  padding: "10px 20px",
                  backgroundColor: deleteLoading ? "#666" : "#f44336",
                  color: "#fff",
                  border: "none",
                  borderRadius: "6px",
                  cursor: deleteLoading ? "not-allowed" : "pointer",
                  fontSize: "16px",
                  fontWeight: 500,
                  transition: "all 0.3s ease",
                }}
                onMouseEnter={(e) => {
                  if (!deleteLoading) {
                    e.currentTarget.style.backgroundColor = "#d32f2f";
                  }
                }}
                onMouseLeave={(e) => {
                  if (!deleteLoading) {
                    e.currentTarget.style.backgroundColor = "#f44336";
                  }
                }}
              >
                {deleteLoading ? "Deletando..." : "Deletar"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ConteudosTab;
